import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../lib/config';
import { track } from '../lib/analytics';

const QUESTION_BACKGROUNDS = {
  1: '#0d1f2d',  // deep ocean blue - environment
  2: '#1a0d2e',  // deep purple - crowds
  3: '#0d2318',  // deep forest green - pace
  4: '#2d1a0d',  // deep amber - activity
  5: '#1a1a0d',  // deep olive - interests
  6: '#0d1a2d',  // deep navy - companions
  7: '#2d0d1a',  // deep burgundy - explorer tendency
  8: '#1f1a0d',  // deep warm gold - food
  9: '#0d2d2d',  // deep teal - evenings
  10: '#1a2d0d', // deep sage - wellness
  11: '#2d1f0d', // deep terracotta - history
  12: '#0d0d2d', // deep midnight - energy
  13: '#2d2d0d', // deep mustard - destination type
  14: '#1a0d1a', // deep violet - social
  15: '#0d1a1a', // deep slate - travel personality
  16: '#1a2d1a', // deep green - family
  17: '#2d1a2d', // deep plum - children ages
};

function Quiz() {
  const navigate = useNavigate();
  const [familyPreQuiz, setFamilyPreQuiz] = useState(false);
  const [familyQuestions, setFamilyQuestions] = useState([]);
  const [familyAnswers, setFamilyAnswers] = useState({});
  const [familyIndex, setFamilyIndex] = useState(0);
  const [familyTransitioning, setFamilyTransitioning] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [transitioning, setTransitioning] = useState(false);

  // Fetch questions from the API on load
  useEffect(() => {
    fetch(`${API_BASE}/quiz/questions`)
      .then(res => res.json())
      .then(data => {
        setQuestions(data.questions);
        setLoading(false);
      })
      .catch(() => {
        setError('Could not load quiz questions. Make sure the API is running.');
        setLoading(false);
      });
  }, []);

  // Filter out conditional questions that don't apply
  const visibleQuestions = questions.filter(q => {
    if (!q.conditional) return true;
    const triggerAnswer = answers[String(q.conditional_question_id)];
    return triggerAnswer === q.conditional_answer;
  });

  const currentQuestion = visibleQuestions[currentIndex];
  const totalQuestions = visibleQuestions.length;
  const progress = totalQuestions > 0 ? ((currentIndex + 1) / totalQuestions) * 100 : 0;
  const bgColor = QUESTION_BACKGROUNDS[currentQuestion?.id] || '#0d0d1f';

  function handleSingleSelect(letter) {
    const qId = String(currentQuestion.id);
    setAnswers(prev => ({ ...prev, [qId]: letter }));
  }

  function handleMultiSelect(letter) {
    const qId = String(currentQuestion.id);
    const maxSelections = currentQuestion.max_selections || 2;
    const current = answers[qId] || [];

    if (current.includes(letter)) {
      // Deselect
      setAnswers(prev => ({
        ...prev,
        [qId]: current.filter(l => l !== letter)
      }));
    } else if (current.length < maxSelections) {
      // Select
      setAnswers(prev => ({
        ...prev,
        [qId]: [...current, letter]
      }));
    }
  }

  function isSelected(letter) {
    const qId = String(currentQuestion?.id);
    const answer = answers[qId];
    if (!answer) return false;
    if (Array.isArray(answer)) return answer.includes(letter);
    return answer === letter;
  }

  function hasAnswer() {
    const qId = String(currentQuestion?.id);
    const answer = answers[qId];
    if (!answer) return false;
    if (Array.isArray(answer)) return answer.length > 0;
    return true;
  }

  function goNext() {
    if (!hasAnswer()) return;
    if (currentIndex < visibleQuestions.length - 1) {
      setTransitioning(true);
      setTimeout(() => {
        setCurrentIndex(prev => prev + 1);
        setTransitioning(false);
      }, 250);
    } else {
      handleSubmit();
    }
  }

  function familyHasAnswer() {
    if (!familyQuestions[familyIndex]) return false;
    const q = familyQuestions[familyIndex];
    const ans = familyAnswers[q.id];
    if (!ans) return false;
    if (Array.isArray(ans)) return ans.length > 0;
    return true;
  }

  function familyHandleSelect(letter) {
    const q = familyQuestions[familyIndex];
    if (!q) return;
    if (q.multi_select) {
      const current = familyAnswers[q.id] || [];
      const maxSel = q.max_selections || 4;
      if (current.includes(letter)) {
        setFamilyAnswers(prev => ({ ...prev, [q.id]: current.filter(l => l !== letter) }));
      } else if (current.length < maxSel) {
        setFamilyAnswers(prev => ({ ...prev, [q.id]: [...current, letter] }));
      }
    } else {
      setFamilyAnswers(prev => ({ ...prev, [q.id]: letter }));
    }
  }

  function familyGoNext() {
    if (!familyHasAnswer()) return;
    if (familyIndex < familyQuestions.length - 1) {
      setFamilyTransitioning(true);
      setTimeout(() => {
        setFamilyIndex(prev => prev + 1);
        setFamilyTransitioning(false);
      }, 250);
    } else {
      // Family pre-quiz complete — now submit everything
      handleFamilySubmit();
    }
  }

  function familyGoBack() {
    if (familyIndex > 0) {
      setFamilyTransitioning(true);
      setTimeout(() => {
        setFamilyIndex(prev => prev - 1);
        setFamilyTransitioning(false);
      }, 250);
    } else {
      // Go back to main quiz
      setFamilyPreQuiz(false);
    }
  }

  async function handleFamilySubmit() {
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/quiz/score`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(answers),
      });
      if (!res.ok) {
        throw new Error(`Quiz scoring failed with status ${res.status}`);
      }
      const profile = await res.json();
      track('Quiz Completed', { family_flow: false });

      sessionStorage.setItem('carpeWonderProfile', JSON.stringify(profile));
      if (localStorage.getItem('carpeKeepSignedIn') === 'true') {
        localStorage.setItem('carpeWonderProfile', JSON.stringify(profile));
      }
      // Auto-save profile to Supabase if user is logged in
      const userId = sessionStorage.getItem('carpeUserId');
      const userEmail = sessionStorage.getItem('carpeUserEmail');
      if (userId) {
        try {
          const saveRes = await fetch(`${API_BASE}/auth/profile/save`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: userId,
              email: userEmail,
              profile: profile,
              micro_answers: {},
            }),
          });
          if (!saveRes.ok) {
            console.error('Profile save to Supabase failed:', saveRes.status, await saveRes.text());
          }
        } catch (saveErr) {
          console.error('Profile save failed but continuing:', saveErr);
        }
      }
      sessionStorage.removeItem('carpeWasReturning');
      sessionStorage.removeItem('carpeSavedDestinations');
      sessionStorage.removeItem('carpeReturningUser');
      sessionStorage.setItem('carpeLoadingContext', JSON.stringify({
        type: 'destinations',
        endpoint: '/recommendations/destinations',
        payload: profile,
        destination: '/results',
        loadingMessage: 'Finding your family destinations...',
      }));
      navigate('/loading');
    } catch {
      setError('Something went wrong. Please try again.');
      setSubmitting(false);
    }
  }

  function goBack() {
    if (currentIndex > 0) {
      setTransitioning(true);
      setTimeout(() => {
        setCurrentIndex(prev => prev - 1);
        setTransitioning(false);
      }, 250);
    } else {
      navigate('/');
    }
  }

  async function handleSubmit() {
    // If Q18 answer is "a" (family path) and we haven't done the family pre-quiz yet,
    // load and show the 6 family questions before scoring
    const q18Answer = answers['18'];
    if (q18Answer === 'a' && !familyPreQuiz) {
      try {
        const res = await fetch(`${API_BASE}/quiz/family-pre-quiz`);
        const data = await res.json();
        setFamilyQuestions(data.questions);
        setFamilyPreQuiz(true);
        setFamilyIndex(0);
        setFamilyAnswers({});
      } catch {
        setError('Could not load family questions. Please try again.');
      }
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/quiz/score`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(answers),
      });
      const profile = await res.json();
      track('Quiz Completed', { family_flow: false });

      sessionStorage.setItem('carpeWonderProfile', JSON.stringify(profile));
      if (localStorage.getItem('carpeKeepSignedIn') === 'true') {
        localStorage.setItem('carpeWonderProfile', JSON.stringify(profile));
      }

      // Auto-save profile to Supabase if user is logged in
      const userId = sessionStorage.getItem('carpeUserId');
      const userEmail = sessionStorage.getItem('carpeUserEmail');
      if (userId) {
        try {
          await fetch(`${API_BASE}/auth/profile/save`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: userId,
              email: userEmail,
              profile: profile,
              micro_answers: {},
            }),
          });
        } catch {
          console.log('Profile save failed but continuing');
        }
      }

      // Clear returning user flags
      sessionStorage.removeItem('carpeWasReturning');
      sessionStorage.removeItem('carpeSavedDestinations');
      sessionStorage.removeItem('carpeReturningUser');

      // Set loading context and navigate to shared loading screen
      sessionStorage.setItem('carpeLoadingContext', JSON.stringify({
        type: 'destinations',
        endpoint: '/recommendations/destinations',
        payload: profile,
        destination: '/results',
        loadingMessage: 'Finding your destinations...',
      }));

      navigate('/loading');
    } catch (err) {
      console.error('Quiz submission error:', err);
      setError('Something went wrong submitting your answers. Please try again.');
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div style={styles.loadingPage}>
        <div style={styles.loadingText}>Loading your quiz...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.loadingPage}>
        <div style={styles.errorText}>{error}</div>
        <button style={styles.retryBtn} onClick={() => navigate('/')}>
          Go back
        </button>
      </div>
    );
  }

  // Family pre-quiz rendering
  if (familyPreQuiz && familyQuestions.length > 0) {
    const fq = familyQuestions[familyIndex];
    const fIsMulti = fq?.multi_select;
    const fProgress = ((familyIndex + 1) / familyQuestions.length) * 100;
    const fSelected = (letter) => {
      const ans = familyAnswers[fq?.id];
      if (!ans) return false;
      if (Array.isArray(ans)) return ans.includes(letter);
      return ans === letter;
    };
    return (
      <div style={{ ...styles.page, background: '#1a0d2e', transition: 'background 0.6s ease' }}>
        <div style={styles.header}>
          <button style={styles.backBtn} onClick={familyGoBack}>← Back</button>
          <div style={styles.progressText}>{familyIndex + 1} of {familyQuestions.length}</div>
        </div>
        <div style={styles.progressTrack}>
          <div style={{ ...styles.progressFill, width: `${fProgress}%` }} />
        </div>
        <div style={{
          ...styles.questionWrap,
          opacity: familyTransitioning ? 0 : 1,
          transition: 'opacity 0.25s ease',
        }}>
          <div style={styles.partLabel}>Your family trip</div>
          <h2 style={styles.questionText}>{fq?.text}</h2>
          {fq?.note && (
            <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', marginBottom: '12px' }}>
              {fq.note}
            </div>
          )}
          {fIsMulti && (
            <div style={styles.multiHint}>Select up to {fq?.max_selections}</div>
          )}
          <div style={styles.optionsList}>
            {fq && Object.entries(fq.options).map(([letter, text]) => {
              const selected = fSelected(letter);
              return (
                <button
                  key={letter}
                  style={{
                    ...styles.option,
                    borderColor: selected ? 'var(--gold)' : 'rgba(255,255,255,0.15)',
                    background: selected ? 'rgba(201,168,76,0.12)' : 'rgba(255,255,255,0.04)',
                    color: selected ? 'var(--gold)' : 'rgba(255,255,255,0.75)',
                  }}
                  onClick={() => familyHandleSelect(letter)}
                >
                  <span style={styles.optionLetter}>{letter.toUpperCase()}</span>
                  <span style={styles.optionText}>{text}</span>
                </button>
              );
            })}
          </div>
          <button
            style={{
              ...styles.nextBtn,
              opacity: familyHasAnswer() ? 1 : 0.4,
              cursor: familyHasAnswer() ? 'pointer' : 'default',
            }}
            onClick={familyGoNext}
            disabled={!familyHasAnswer() || submitting}
          >
            {familyIndex === familyQuestions.length - 1
              ? (submitting ? 'Finding your family destinations...' : 'Find my family destinations →')
              : 'Next →'}
          </button>
        </div>
      </div>
    );
  }

  if (!currentQuestion) return null;

  const isMulti = currentQuestion.multi_select;
  const optionEntries = Object.entries(currentQuestion.options);

  return (
    <div style={{ ...styles.page, background: bgColor, transition: 'background 0.6s ease' }}>

      {/* Header */}
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={goBack}>
          ← Back
        </button>
        <div style={styles.progressText}>
          {currentIndex + 1} of {totalQuestions}
        </div>
      </div>

      {/* Progress bar */}
      <div style={styles.progressTrack}>
        <div style={{ ...styles.progressFill, width: `${progress}%` }} />
      </div>

      {/* Question */}
      <div style={{
        ...styles.questionWrap,
        opacity: transitioning ? 0 : 1,
        transition: 'opacity 0.25s ease',
      }}>
        <div style={styles.partLabel}>
          {currentQuestion.part === 1 && 'Where you go'}
          {currentQuestion.part === 2 && 'How you experience it'}
          {currentQuestion.part === 3 && 'What excites you'}
          {currentQuestion.part === 4 && 'Your travel group'}
        </div>

        {/* Question text */}
        <h2 style={styles.questionText}>
          {currentQuestion.conditional_trigger &&
           answers[currentQuestion.conditional_trigger.question_id] === currentQuestion.conditional_trigger.answer &&
           currentQuestion.conditional_text?.family
            ? currentQuestion.conditional_text.family
            : currentQuestion.text}
        </h2>
        {/* Note for Q18 */}
        {currentQuestion.id === 18 && currentQuestion.note && (
          <div style={{
            fontSize: '15px',
            color: 'rgba(255,255,255,0.85)',
            marginBottom: '20px',
            lineHeight: '1.6',
            padding: '12px 16px',
            background: 'rgba(201,168,76,0.08)',
            border: '1px solid rgba(201,168,76,0.25)',
            borderRadius: '10px',
          }}>
            {currentQuestion.note}
          </div>
        )}
        {/* Multi-select hint */}
        {isMulti && (
          <div style={styles.multiHint}>
            Select up to {currentQuestion.max_selections}
          </div>
        )}

        {/* Options */}
        <div style={styles.optionsList}>
          {optionEntries.map(([letter, text]) => {
            const selected = isSelected(letter);
            return (
              <button
                key={letter}
                style={{
                  ...styles.option,
                  borderColor: selected ? 'var(--gold)' : 'rgba(255,255,255,0.15)',
                  background: selected ? 'rgba(201,168,76,0.12)' : 'rgba(255,255,255,0.04)',
                  color: selected ? 'var(--gold)' : 'rgba(255,255,255,0.75)',
                }}
                onClick={() => isMulti ? handleMultiSelect(letter) : handleSingleSelect(letter)}
              >
                <span style={styles.optionLetter}>{letter.toUpperCase()}</span>
                <span style={styles.optionText}>{text}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Next / Submit button */}
      <div style={styles.footer}>
        <button
          style={{
            ...styles.nextBtn,
            opacity: hasAnswer() ? 1 : 0.35,
            cursor: hasAnswer() ? 'pointer' : 'not-allowed',
          }}
          onClick={goNext}
          disabled={!hasAnswer() || submitting}
        >
          {submitting
            ? 'Finding your destinations...'
            : currentIndex === totalQuestions - 1
            ? 'Find my destinations →'
            : 'Next →'}
        </button>
      </div>

    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    padding: '0',
  },
  loadingPage: {
    minHeight: '100vh',
    background: '#0d0d1f',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
  },
  loadingText: {
    color: 'var(--gold)',
    fontSize: '16px',
    letterSpacing: '0.05em',
  },
  errorText: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: '15px',
    textAlign: 'center',
    maxWidth: '300px',
    lineHeight: '1.6',
  },
  retryBtn: {
    background: 'transparent',
    border: '1px solid var(--gold)',
    borderRadius: '20px',
    color: 'var(--gold)',
    padding: '12px 28px',
    fontSize: '14px',
    minHeight: '44px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 20px 12px',
  },
  backBtn: {
    background: 'transparent',
    border: 'none',
    color: 'rgba(255,255,255,0.7)',
    fontSize: '15px',
    padding: '8px 0',
    minHeight: '44px',
  },
  progressText: {
    fontSize: '13px',
    color: 'rgba(255,255,255,0.6)',
    letterSpacing: '0.05em',
  },
  progressTrack: {
    height: '3px',
    background: 'rgba(255,255,255,0.1)',
    margin: '0 20px',
    borderRadius: '2px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    background: 'var(--gold)',
    borderRadius: '2px',
    transition: 'width 0.4s ease',
  },
  questionWrap: {
    flex: 1,
    padding: '32px 20px 20px',
    display: 'flex',
    flexDirection: 'column',
  },
  partLabel: {
    fontSize: '12px',
    color: 'rgba(201,168,76,0.8)',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    marginBottom: '12px',
  },
  questionText: {
    fontSize: '20px',
    fontWeight: '500',
    color: '#fff',
    lineHeight: '1.4',
    marginBottom: '10px',
  },
  multiHint: {
    fontSize: '13px',
    color: 'rgba(255,255,255,0.6)',
    marginBottom: '20px',
  },
  optionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginTop: '8px',
  },
  option: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    border: '1px solid',
    borderRadius: '12px',
    padding: '14px 16px',
    textAlign: 'left',
    transition: 'all 0.2s ease',
    width: '100%',
    minHeight: '44px',
  },
  optionLetter: {
    fontSize: '13px',
    fontWeight: '600',
    minWidth: '20px',
    marginTop: '1px',
    opacity: 0.7,
  },
  optionText: {
    fontSize: '15px',
    lineHeight: '1.5',
  },
  footer: {
    padding: '16px 20px 32px',
  },
  nextBtn: {
    width: '100%',
    background: 'var(--gold)',
    color: '#0d0d1f',
    border: 'none',
    borderRadius: '28px',
    padding: '16px',
    fontSize: '15px',
    fontWeight: '600',
    letterSpacing: '0.02em',
    transition: 'opacity 0.2s ease',
    minHeight: '44px',
  },
};

export default Quiz;